import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import moment from "moment";
import {useSelector} from "react-redux";
import Alert from "@mui/material/Alert";
import {getDsContext} from "@/utils/dsContext";
import {useRosterLineup, flushLineup} from "@/utils/rosterLineupData";
import {RosterLineupAPI} from "@/api/roster_lineup";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import MainActions from "@/components/MainActions";
import ScoutPlayerTile from "@/components/ScoutPlayerTile";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import {PlayerAPI} from "@/api/player";
import {isMonitored, useScoutingPipeline} from "@/utils/scoutingPipeline";
import {
	autoAssign,
	lineupRoleDepartment,
	buildModuleSlots,
	playerAge,
	promote,
	reassignStacks,
	sortBench,
	sortCandidates,
	usedPlayerIds,
} from "@/utils/rosterLineup";
import {ERROR_OPERATION} from "@/utils/constants";

import "./index.scss";

const playerLabel = (p) =>
	[p?.lastName, p?.firstName].filter(Boolean).join(" ");

// Scadenza del contratto: rosso entro 12 mesi, arancio entro 24, verde oltre.
const contractColor = (contract) => {
	const months = moment(contract.endDate).diff(moment(), "months");
	if (months < 0) return "#9ca3af";
	if (months <= 12) return "#ef4444";
	if (months <= 24) return "#f59e0b";
	return "#22c55e";
};

function PlayerMeta({player, lineup, extras}) {
	const age = playerAge(player);
	const jersey = extras?.jerseys?.get(player?.id);
	const contract = extras?.contracts?.[player?.id];
	return (
		<span className="lineup-meta">
			{[
				jersey && "#" + jersey,
				lineup.showNationality && player?.nationality,
				lineup.showAge && age !== null && age + " anni",
			]
				.filter(Boolean)
				.join(" · ")}
			{contract?.endDate && (
				<span
					className="lineup-meta__contract"
					style={{color: contractColor(contract)}}
					title={
						"Contratto fino al " + moment(contract.endDate).format("DD/MM/YYYY")
					}
				>
					{" '" + moment(contract.endDate).format("YY")}
				</span>
			)}
		</span>
	);
}

// Senza testo propone la panchina e i monitorati; con il testo cerca in tutto il database.
// Con rosterOnly (squadra reale) si sceglie solo tra i giocatori della rosa.
function PlayerPicker({
	open,
	title,
	used,
	bench,
	rosterIds,
	rosterOnly,
	seasonId,
	onPick,
	onClose,
	handleNotification,
}) {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const {pipeline} = useScoutingPipeline({enabled: open && !rosterOnly});
	const monitoredIds = useMemo(
		() =>
			new Set(
				Object.values(pipeline)
					.filter(isMonitored)
					.map((e) => e.playerId),
			),
		[pipeline],
	);
	const timer = useRef(null);

	useEffect(() => {
		if (!open) return;
		clearTimeout(timer.current);
		let active = true;
		if (rosterOnly) {
			const key = query.trim().toLowerCase();
			setResults(
				bench.filter((p) => !key || playerLabel(p).toLowerCase().includes(key)),
			);
			return;
		}
		timer.current = setTimeout(async () => {
			setSearching(true);
			try {
				const found = query.trim()
					? sortCandidates(
							(
								await PlayerAPI.paginated({
									page: 1,
									pageSize: 30,
									search: query.trim(),
									seasonId,
								})
							)?.data || [],
							monitoredIds,
						)
					: [
							...sortCandidates(bench, monitoredIds),
							...sortCandidates(
								Object.values(pipeline)
									.filter((e) => isMonitored(e) && !rosterIds.has(e.playerId))
									.slice(0, 30)
									.map((e) => e.player)
									.filter(Boolean),
								monitoredIds,
							),
						];
				if (active) setResults(found);
			} catch {
				if (active) {
					setResults([]);
					handleNotification(ERROR_OPERATION, "error");
				}
			} finally {
				if (active) setSearching(false);
			}
		}, 280);
		return () => {
			active = false;
			clearTimeout(timer.current);
		};
	}, [open, query, monitoredIds]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!open) setQuery("");
	}, [open]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle className="lineup-dialog-title">
				<span>{title}</span>
				<IconButton size="small" aria-label="chiudi" onClick={onClose}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<VLFTextField
					placeholder={
						rosterOnly
							? "Cerca per nome nella rosa"
							: "Cerca per nome in tutto il database"
					}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					size="small"
					inputProps={{"aria-label": "Cerca giocatore", autoFocus: true}}
				/>
				{searching && <div className="lineup-hint">Ricerca in corso…</div>}
				{!searching && !results.length && (
					<div className="lineup-hint">
						{query.trim()
							? "Nessun giocatore trovato."
							: rosterOnly
								? "Panchina vuota: tutta la rosa è schierata."
								: "Panchina vuota e nessun monitorato: cerca per nome."}
					</div>
				)}
				<div className="lineup-results">
					{results.map((p) => {
						const taken = used.has(p.id);
						const tag = taken
							? "Già schierato"
							: rosterIds.has(p.id)
								? "In rosa"
								: null;
						return (
							<ScoutPlayerTile
								key={p.id}
								player={p}
								pipelineEntry={pipeline[p.id]}
								onClick={taken ? undefined : () => onPick(p)}
								action={
									tag && <span className="lineup-result__tag">{tag}</span>
								}
							/>
						);
					})}
				</div>
			</DialogContent>
		</Dialog>
	);
}

const esc = (s) =>
	String(s ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

const printLineup = ({roster, module, slots, stacks, bench, playersById}) => {
	const lines = [...new Set(slots.map((s) => s.y))].sort((a, b) => a - b);
	const cell = (slot) => {
		const [starter, ...others] = (stacks[slot.id] || []).map((id) =>
			playersById.get(id),
		);
		return `<div class="slot"><b>${esc(slot.code)}</b><span>${esc(
			starter?.lastName || "—",
		)}</span>${others
			.map((p) => `<small>${esc(p?.lastName)}</small>`)
			.join("")}</div>`;
	};
	const field = lines
		.map(
			(y) =>
				`<div class="row">${slots
					.filter((s) => s.y === y)
					.sort((a, b) => a.x - b.x)
					.map(cell)
					.join("")}</div>`,
		)
		.join("");
	const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>${esc(roster.name || "Formazione")}</title>
<style>
body{font-family:system-ui,sans-serif;margin:12mm;color:#0b1f44;print-color-adjust:exact;-webkit-print-color-adjust:exact}
header{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:8mm}
h1{font-size:18px;margin:0}
.field{background:#2f7a3f;border-radius:6px;padding:6mm 4mm;display:flex;flex-direction:column;gap:6mm;width:130mm}
.row{display:flex;justify-content:space-around}
.slot{background:#fff;border-radius:4px;padding:2mm;width:24mm;display:flex;flex-direction:column;align-items:center;font-size:11px}
.slot b{font-size:9px;color:#64748b}.slot small{color:#64748b}
h2{font-size:12px;text-transform:uppercase;color:#64748b;margin:8mm 0 2mm}
p{font-size:12px;margin:0}
</style></head><body>
<header><h1>${esc(roster.name)}</h1><span>${esc(
		[roster.season?.name, module?.name, moment().format("DD/MM/YYYY")]
			.filter(Boolean)
			.join(" · "),
	)}</span></header>
<div class="field">${field}</div>
<h2>Panchina</h2><p>${bench.map((p) => esc(playerLabel(p))).join(", ") || "—"}</p>
<script>window.onload=()=>{window.print();}</script>
</body></html>`;
	const w = window.open("", "_blank", "width=1100,height=750");
	if (w) {
		w.document.write(html);
		w.document.close();
	}
};

// La stampa si avvia dalla pagina che ospita il campo (ref.print()).
const RosterLineupEditor = forwardRef(function RosterLineupEditor(
	{
		roster,
		modules,
		onAddPlayer,
		onOpenPlayer,
		handleNotification,
		showJerseyNumbers,
		contracts,
	},
	ref,
) {
	const rosterModules = useMemo(
		() => (roster?.modules || []).map((m) => m.module).filter(Boolean),
		[roster],
	);
	const source = useRosterLineup(roster.id);
	const options =
		source.data?.modules ||
		(rosterModules.length ? rosterModules : modules || []);
	const players = useMemo(
		() => (roster?.players || []).map((p) => p.player).filter(Boolean),
		[roster],
	);
	const playersById = useMemo(
		() => new Map(players.map((p) => [p.id, p])),
		[players],
	);
	const extras = useMemo(
		() => ({
			jerseys: showJerseyNumbers
				? new Map(
						(roster?.players || [])
							.filter((p) => p.player && p.jerseyNumber)
							.map((p) => [p.player.id, p.jerseyNumber]),
					)
				: null,
			contracts,
		}),
		[roster, showJerseyNumbers, contracts],
	);
	const saved = source.data;
	const defaultId =
		saved?.defaultModuleId ||
		roster.modules?.find((m) => m.isDefault)?.moduleId;
	const initialModule =
		options.find((m) => m.id === defaultId) ||
		options.find((m) => m.name === "4-3-3") ||
		options[0];
	// Automatic first arrangement is a preview: reading never writes to the server.
	const preview = useMemo(
		() =>
			initialModule
				? {
						moduleId: initialModule.id,
						stacks: autoAssign(buildModuleSlots(initialModule), players),
						showAge: true,
						showNationality: true,
					}
				: null,
		[initialModule, players],
	);
	const lineup = saved?.exists || source.pending ? saved : preview;
	const canWrite =
		source.status === "ready" && saved?.canWrite && !source.conflict;
	const [openSlot, setOpenSlot] = useState(null);
	const [picker, setPicker] = useState(null);
	const [adding, setAdding] = useState(false);
	const update = (changes) => {
		const next = {...lineup, ...changes};
		const module = options.find((m) => m.id === next.moduleId);
		if (module)
			source.change({
				...next,
				slotCodes: Object.fromEntries(
					buildModuleSlots(module).map((s) => [s.id, s.code]),
				),
			});
	};

	const module = options.find((m) => m.id === lineup?.moduleId) || null;
	const slots = useMemo(() => buildModuleSlots(module), [module]);
	// Chi è uscito dalla rosa sparisce anche dal campo.
	const stacks = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(lineup?.stacks || {}).map(([slotId, ids]) => [
					slotId,
					ids.filter((id) => playersById.has(id)),
				]),
			),
		[lineup, playersById],
	);
	const used = useMemo(() => usedPlayerIds(stacks), [stacks]);
	const bench = useMemo(
		() => sortBench(players.filter((p) => !used.has(p.id))),
		[players, used],
	);

	useImperativeHandle(ref, () => ({
		print: () => {
			if (source.status !== "ready" || source.pending) {
				handleNotification(
					"Attendi il caricamento e il salvataggio dello schieramento prima di stampare",
					"info",
				);
				return;
			}
			if (module)
				printLineup({roster, module, slots, stacks, bench, playersById});
		},
	}));

	if (source.status === "idle" || source.status === "loading")
		return <p className="lineup-hint">Caricamento schieramento…</p>;
	if (source.status === "error")
		return (
			<Alert severity="error">
				Schieramento non disponibile.{" "}
				<MainActions actions={[{label: "Riprova", onClick: source.refresh}]} />
			</Alert>
		);
	if (!module)
		return (
			<Alert severity="warning">
				{saved?.exists
					? "Il modulo salvato non è più disponibile. Scegli un modulo per riorganizzare lo schieramento."
					: "Nessun modulo disponibile."}
				{canWrite && options.length > 0 && (
					<MainActions
						actions={[
							{
								label: "Usa modulo disponibile",
								onClick: () =>
									update({
										...preview,
										stacks: reassignStacks(
											saved.slots.map((s) => ({
												id: s.slotId,
												code: s.code,
												department: lineupRoleDepartment(s.code),
											})),
											saved.stacks,
											buildModuleSlots(initialModule),
										).stacks,
									}),
							},
						]}
					/>
				)}
			</Alert>
		);

	const changeModule = (moduleId) => {
		const target = options.find((m) => m.id === moduleId);
		const {stacks: next, bench: out} = reassignStacks(
			slots,
			stacks,
			buildModuleSlots(target),
		);
		update({moduleId, stacks: next});
		if (out.length)
			handleNotification(
				out.length + " in panchina: nessun ruolo compatibile",
				"info",
			);
	};

	const pick = async (player) => {
		if (!canWrite || adding || used.has(player.id)) return;
		const context = getDsContext();
		const slotId = picker;
		setAdding(true);
		try {
			await flushLineup(roster.id);
			if (!playersById.has(player.id)) {
				if (roster.prospect !== true)
					throw Error("Scegli un giocatore della rosa");
				await RosterLineupAPI.addPlayer(roster.id, player.id);
				if (context !== getDsContext()) return;
				await onAddPlayer?.(player);
			}
			if (context !== getDsContext()) return;
			setPicker(null);
			update({
				stacks: {...stacks, [slotId]: [...(stacks[slotId] || []), player.id]},
			});
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			if (context === getDsContext()) setAdding(false);
		}
	};

	const setStack = (slotId, stack) =>
		update({stacks: {...stacks, [slotId]: stack}});

	const slot = slots.find((s) => s.id === openSlot);
	const stack = (slot && stacks[slot.id]) || [];

	return (
		<div className="roster-lineup">
			{source.error && (
				<Alert severity="error">
					{source.conflict
						? "Rosa o schieramento cambiati. Ricarica dal server per aggiornarti; la bozza locale verrà scartata."
						: "Modifiche non salvate. La bozza resta disponibile per riprovare."}
					<MainActions
						actions={[
							{
								label: source.conflict
									? "Ricarica dal server"
									: "Riprova salvataggio",
								onClick: source.conflict ? source.refresh : source.retry,
							},
						]}
					/>
				</Alert>
			)}
			<div className="lineup-hint" role="status">
				{source.pending
					? source.saving
						? "Salvataggio in corso…"
						: "Modifiche da salvare"
					: saved?.exists
						? "Schieramento condiviso"
						: "Proposta automatica non ancora salvata"}
			</div>
			{!saved?.exists && !source.pending && canWrite && (
				<MainActions
					actions={[{label: "Salva schieramento", onClick: () => update({})}]}
				/>
			)}
			<fieldset
				disabled={!canWrite || adding}
				style={{border: 0, padding: 0, margin: 0, minWidth: 0}}
			>
				<div className="lineup-settings">
					<div className="lineup-module">
						<VLFSelectNew
							label="Modulo"
							aria-label="Modulo"
							options={options.map((m) => ({value: m.id, label: m.name}))}
							value={module ? {value: module.id, label: module.name} : null}
							onChange={(o) => o && changeModule(o.value)}
							sorted={false}
							isClearable={false}
							noMargin
							isDisabled={!canWrite || adding}
						/>
					</div>
					<FormControlLabel
						className="lineup-check"
						control={
							<Checkbox
								size="small"
								checked={!!lineup.showNationality}
								onChange={(e) => update({showNationality: e.target.checked})}
							/>
						}
						label="Nazionalità"
					/>
					<FormControlLabel
						className="lineup-check"
						control={
							<Checkbox
								size="small"
								checked={!!lineup.showAge}
								onChange={(e) => update({showAge: e.target.checked})}
							/>
						}
						label="Età"
					/>
				</div>
			</fieldset>
			<div className="lineup-pitch">
				<div className="lineup-pitch__lines" />
				{slots.map((s) => {
					const ids = stacks[s.id] || [];
					const starter = playersById.get(ids[0]);
					return (
						<button
							key={s.id}
							type="button"
							className={"lineup-slot" + (starter ? "" : " lineup-slot--empty")}
							style={{"--x": s.x + "%", "--y": s.y + "%"}}
							aria-label={
								s.code + ": " + (starter ? playerLabel(starter) : "vuoto")
							}
							onClick={() => setOpenSlot(s.id)}
						>
							<span className="lineup-slot__code">{s.code}</span>
							<span className="lineup-slot__body">
								<span className="lineup-slot__name">
									{starter ? (
										<>
											{starter.firstName && (
												<span className="lineup-slot__initial">
													{starter.firstName[0]}.{" "}
												</span>
											)}
											{starter.lastName}
										</>
									) : (
										"+"
									)}
								</span>
								{starter && (
									<PlayerMeta
										player={starter}
										lineup={lineup}
										extras={extras}
									/>
								)}
							</span>
							{ids.length > 1 && (
								<span className="lineup-slot__depth">+{ids.length - 1}</span>
							)}
						</button>
					);
				})}
			</div>
			<section className="lineup-bench" aria-label="Panchina">
				<h2>Panchina</h2>
				{!bench.length && (
					<p className="lineup-hint">Tutta la rosa è schierata.</p>
				)}
				<div className="lineup-bench__list">
					{bench.map((p) => (
						<button
							key={p.id}
							type="button"
							className="lineup-chip"
							onClick={() => onOpenPlayer(p)}
						>
							{playerLabel(p)}
							<PlayerMeta player={p} lineup={lineup} extras={extras} />
						</button>
					))}
				</div>
			</section>

			<Dialog
				open={!!slot}
				onClose={() => setOpenSlot(null)}
				fullWidth
				maxWidth="xs"
			>
				{slot && (
					<>
						<DialogTitle className="lineup-dialog-title">
							<span>{slot.code}</span>
							<IconButton
								size="small"
								aria-label="chiudi"
								onClick={() => setOpenSlot(null)}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</DialogTitle>
						<DialogContent>
							{!stack.length && (
								<p className="lineup-hint">Nessun giocatore in questo ruolo.</p>
							)}
							<ol className="lineup-stack">
								{stack.map((id, index) => {
									const player = playersById.get(id);
									return (
										<li key={id} className={index === 0 ? "is-starter" : ""}>
											<button
												type="button"
												className="lineup-stack__name"
												onClick={() => onOpenPlayer(player)}
											>
												{playerLabel(player)}
												<PlayerMeta
													player={player}
													lineup={lineup}
													extras={extras}
												/>
											</button>
											<span className="lineup-stack__label">
												{index === 0 ? "Titolare" : "Alternativa " + index}
											</span>
											<IconButton
												size="small"
												aria-label={"Promuovi " + playerLabel(player)}
												disabled={!canWrite || adding || index === 0}
												onClick={() => setStack(slot.id, promote(stack, index))}
											>
												▲
											</IconButton>
											<IconButton
												size="small"
												aria-label={"Togli dal posto " + playerLabel(player)}
												disabled={!canWrite || adding}
												onClick={() =>
													setStack(
														slot.id,
														stack.filter((x) => x !== id),
													)
												}
											>
												✕
											</IconButton>
										</li>
									);
								})}
							</ol>
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: "Aggiungi giocatore",
										disabled: !canWrite || adding,
										title: "Aggiungi giocatore",
										onClick: () => setPicker(slot.id),
									},
								]}
							/>
						</DialogContent>
					</>
				)}
			</Dialog>

			<PlayerPicker
				open={!!picker}
				title={"Aggiungi · " + (slot?.code || "")}
				used={used}
				bench={bench}
				rosterIds={new Set(playersById.keys())}
				rosterOnly={roster.prospect !== true}
				seasonId={roster.seasonId || roster.season?.id}
				onPick={pick}
				onClose={() => setPicker(null)}
				handleNotification={handleNotification}
			/>
		</div>
	);
});

const RosterLineup = forwardRef(function RosterLineup(props, ref) {
	useSelector((s) => s.user);
	useSelector((s) => s.token);
	useSelector((s) => s.season?.id);
	return (
		<RosterLineupEditor
			key={`${getDsContext()}:${props.roster.id}`}
			{...props}
			ref={ref}
		/>
	);
});
export default RosterLineup;
